import { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";

import { getNextSaturday, getPreviousSunday } from "@utils/general";
import FlowbiteDatepickerInLine from "@components/DatepickerInLine";
import Button from "@components/Button";
import Spinner from "@components/Spinner";
import { IPlantSelect } from "@view/admin/HoursUpload";
import { SelectFilter } from "@components/SelectFilter";
import InputFieldDecimal from "@components/InputFieldDecimal";
import { generateTemplate } from "@service/HoursUploadService";
import { useAuth } from "@components/context/AuthContext";
import { IRoleDTO } from "@interfaces/IRoleDTO";

interface IPayType {
  id: number;
  value: string;
}

interface IGeneratorTemplateProps {
  plants: IPlantSelect[];
  roles: IRoleDTO[];
}

export function GeneratorTemplate({
  plants,
  roles,
}: IGeneratorTemplateProps): JSX.Element {
  const { hasPermission } = useAuth();
  const [generatingTemplate, setGeneratingTemplate] = useState<boolean>(false);
  const [start_date, setStartDate] = useState<Date | null>(
    getPreviousSunday(null, -1)
  );
  const [end_date, setEndDate] = useState<Date | null>(
    getNextSaturday(new Date(), -1)
  );
  const [selectedPlant, setSelectedPlant] = useState<IPlantSelect | null>(null);
  const [addHolidayPay, setAddHolidayPay] = useState<boolean>(false);
  const [holiday_date, setHolidayDate] = useState<Date>(new Date());
  const [payTypes] = useState<Array<IPayType>>([
    { id: 1, value: "Flat Amount" },
    { id: 2, value: "Gifted Hours" },
  ]);
  const [selectedPayType, setSelectedPayType] = useState<IPayType>({
    id: 1,
    value: "Flat Amount",
  });
  const [valueType, setValueType] = useState<string>("");
  const [errorValueType, setErrorValueType] = useState<string>("");
  const disabledPlants = hasPermission(roles, "Hours Edit", "Change Plant")
    ? false
    : true;
  useEffect(() => {
    const selected = plants && plants.length > 0 ? plants[0] : null;
    setSelectedPlant(selected);
  }, [plants]);

  function handleStartDateChange(date: Date) {
    if (date.getDay() !== 0) {
      alert("Please select a Sunday.");
      return;
    }
    setStartDate(date);
    const nextSaturday = getNextSaturday(date ? date : new Date());
    setEndDate(nextSaturday);
  }

  function handleEndDateChange(date: Date | null) {
    if (!date) {
      setEndDate(null);
      return;
    }
    if (date.getDay() !== 6) {
      alert("Please select a Saturday.");
      return;
    }
    const previousSunday = getPreviousSunday(date);
    handleStartDateChange(previousSunday);
  }

  function handleHolidayDateChange(date: Date | null) {
    if (!date) {
      return;
    }
    setHolidayDate(date);
  }

  function handleValueTypeChange(event: React.ChangeEvent<HTMLInputElement>) {
    if (event.target.value === "" || Number(event.target.value) <= 0) {
      setErrorValueType("This field is required");
    } else {
      setErrorValueType("");
    }
    setValueType(event.target.value);
  }

  async function handleGenerateTemplate() {
    try {
      //Validations
      if (!selectedPlant) {
        console.error("Plant not found");
        return;
      }
      if (!start_date || !end_date) {
        alert("Please select a valid start and end date.");
        return;
      }
      if (addHolidayPay) {
        if (!holiday_date) {
          alert("Please select a valid holiday date.");
          return;
        }
        if (Number(valueType) <= 0) {
          setErrorValueType("This field is required");
          alert("Please inform a valid holiday pay amount or gifted hours.");
          return;
        }
      }
      // const data = {
      //   start_date: format(start_date, "yyyy-MM-dd"),
      //   end_date: format(end_date, "yyyy-MM-dd"),
      //   plant_id: selectedPlant.id?.toString() || "",
      //   // eslint-disable-next-line
      //   ...(addHolidayPay ? { holi_date: format(holiday_date, "yyyy-MM-dd"), pay_type: selectedPayType.value, ...(selectedPayType.value==="Flat Amount" ? { ["flat_amount"]: valueType } : { ["gifted_hours"]: valueType }) } : {})
      // };
      let holidayPayFields = {};
      if (addHolidayPay) {
        holidayPayFields = {
          holi_date: format(holiday_date, "yyyy-MM-dd"),
          pay_type: selectedPayType.value,
        };
        if (selectedPayType.value === "Flat Amount") {
          holidayPayFields = {
            ...holidayPayFields,
            flat_amount: valueType,
          };
        } else {
          holidayPayFields = {
            ...holidayPayFields,
            gifted_hours: valueType,
          };
        }
      }
      const data = {
        start_date: format(start_date, "yyyy-MM-dd"),
        end_date: format(end_date, "yyyy-MM-dd"),
        plant_id: selectedPlant.id?.toString() || "",
        ...holidayPayFields,
      };
      setGeneratingTemplate(true);
      const response = await generateTemplate(data);

      const url = window.URL.createObjectURL(response.data as any);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        response.headers["content-disposition"]?.split("filename=")[1] ??
          "attachment.xlsx"
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download error:", err);
    } finally {
      setGeneratingTemplate(false);
    }
  }

  function handleChangePlant(value: IPlantSelect) {
    setSelectedPlant(value);
  }

  function handleChangePayType(value: IPayType) {
    setSelectedPayType(value);
  }

  const handleAddHolidayPay = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        setAddHolidayPay(true);
      } else {
        setAddHolidayPay(false);
      }
    },
    []
  );

  return (
    <div className="tw-flex tw-flex-col *:tw-mb-2 tw-h-[50vh] tw-w-[50rem]">
      <h4 className="Subheader tw-mb-7">Generate Template</h4>
      <div className="tw-flex tw-flex-row tw-items-center tw-mb-5 tw-ml-4 tw-mt-4 tw-w-[23.8rem]">
        <div className="tw-w-10">Plant:</div>
        <SelectFilter
          className="tw-ml-1 tw-w-[22rem]"
          select={plants.length > 0 ? plants[0] : { id: 0, value: "" }}
          options={plants}
          onSelect={handleChangePlant}
          placeholder="Select a plant"
          disabled={disabledPlants}
        />
      </div>
      <div className="tw-flex tw-flex-row tw-items-center tw-mb-5 tw-ml-4 tw-mt-3">
        <span className="tw-w-10">Start:</span>
        <FlowbiteDatepickerInLine
          name={undefined}
          value={start_date}
          handleOnChange={handleStartDateChange}
          handleOnBlur={undefined}
          className="tw-w-[21.3rem]"
        />
        <span className="tw-w-10 tw-ml-4">End:</span>
        <FlowbiteDatepickerInLine
          name={undefined}
          value={end_date}
          handleOnChange={handleEndDateChange}
          handleOnBlur={undefined}
          className="tw-w-[21.1rem]"
        />
      </div>
      <div className="tw-flex tw-justify-left tw-ml-4 tw-items-center tw-mb-4 tw-mt-5">
        <input
          checked={addHolidayPay}
          type="checkbox"
          className="tw-flex tw-flex-row"
          id="add-pay-stub"
          name="add_pay_stub"
          onChange={handleAddHolidayPay}
        />
        <span className="tw-ml-4">Add Holiday Pay</span>
      </div>
      {!!addHolidayPay && (
        <div className="tw-mt-2 tw-border tw-border-gray-300 tw-rounded-md tw-p-4 tw-mb-4 tw-mx-4 tw-w-[775px]">
          <div className="tw-flex tw-flex-row tw-items-center tw-mb-5 tw-mt-1">
            <span className="tw-w-20">Holiday Date:</span>
            <FlowbiteDatepickerInLine
              name={undefined}
              value={holiday_date}
              handleOnChange={handleHolidayDateChange}
              handleOnBlur={undefined}
              className="tw-w-[280px]"
            />
          </div>
          <div className="tw-flex tw-flex-row tw-items-center tw-mb-5">
            <div className="tw-flex tw-flex-row tw-items-center tw-mb-5 tw-w-[22.5rem]">
              <div className="tw-w-20">Pay Type:</div>
              <SelectFilter
                className="tw-ml-3 tw-w-[21rem]"
                select={selectedPayType}
                options={payTypes}
                onSelect={handleChangePayType}
                placeholder="Select a pay type"
              />
            </div>
            <div className="tw-flex tw-flex-row tw-items-center tw-mb-5 tw-w-[23.5rem]">
              <div className="tw-ml-4 tw-mr-2">
                {" "}
                {selectedPayType.value === "Flat Amount"
                  ? "$ Amount:"
                  : "Gifted Hours:"}
              </div>
              {/* <input value={valueType} className="tw-w-[17rem] tw-h-8 tw-border tw-rounded-md" type="number" id="gifted_hours" name="gifted_hours" step="0.25" onChange={handleValueTypeChange} /> */}
              <InputFieldDecimal
                value={valueType}
                onChange={handleValueTypeChange}
                className="tw-h-[30px] tw-w-64 tw-border tw-rounded-md"
                type="number"
                id="value_type"
                name="value_type"
                step="0.25"
                disabled={false}
                placeholder=""
                errorMsg={errorValueType.length > 0 ? errorValueType : ""}
                ref={undefined}
              />
            </div>
          </div>
        </div>
      )}
      <div className="tw-flex tw-justify-end tw-mt-6 tw-mr-3">
        <Button
          disabled={generatingTemplate}
          className="md:tw-w-[16%]"
          onClick={handleGenerateTemplate}
        >
          {generatingTemplate ? (
            <Spinner className="tw-flex tw-justify-center tw-ml-2 tw-items-center" />
          ) : (
            "Generate Template"
          )}
        </Button>
      </div>
    </div>
  );
}
